import { useDarkMode } from '../DarkModeContext';
import useIsVisible from '../isVisible';
import { useRef } from 'react';

function DiceMould() {
  const { isDarkMode } = useDarkMode();

  const refs = [useRef(), useRef(), useRef()];
  const vis = [
    useIsVisible(refs[0]),
    useIsVisible(refs[1]),
    useIsVisible(refs[2]),
  ];

  const sec = (i) => `px-8 py-12 transition-opacity ease-in duration-700 ${vis[i] ? 'opacity-100' : 'opacity-0'} ${
    i % 2 === 0
      ? isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      : isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
  }`;

  const h2 = `text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-Template-0'}`;
  const hr = `border-t-2 my-4 ${isDarkMode ? 'border-gray-600' : 'border-Template-2'}`;
  const body = `text-base leading-relaxed mb-4 max-w-3xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const li = `leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const steps = [
    {
      n: '01',
      title: '3D-Druck Positivform',
      desc: 'Der Würfel (Ikosaeder) wird in CAD modelliert und als Positivform in PLA oder PETG gedruckt. Diese Form dient als Vorlage für die Silikonform.',
    },
    {
      n: '02',
      title: 'Silikonform gießen',
      desc: 'Die Positivform wird in flüssiges Silikon eingebettet. Nach dem Aushärten entsteht eine flexible Negativform, die exakt die Würfelgeometrie abbildet.',
    },
    {
      n: '03',
      title: 'Elektronik einlegen',
      desc: 'Flex-PCB, LiPo-Akku und QI-Empfängerspule werden passend positioniert und fixiert, bevor das Harz eingefüllt wird.',
    },
    {
      n: '04',
      title: 'Harz vergießen',
      desc: 'Transparentes oder getöntes Gießharz wird in die Silikonform gegossen. Das Harz umschließt die Elektronik vollständig.',
    },
    {
      n: '05',
      title: 'Entformen & Finish',
      desc: 'Nach der Aushärtung lässt sich der Würfel dank der flexiblen Silikonform leicht entnehmen. Kanten werden bei Bedarf nachbearbeitet.',
    },
  ];

  return (
    <>
      <div ref={refs[0]} className={sec(0)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Vorgehen</h2>
          <hr className={hr} />
          <p className={body}>
            Das Gehäuse entsteht in einem zweistufigen Prozess: Zuerst wird eine{' '}
            <strong>Positivform aus dem 3D-Drucker</strong> gefertigt. Daraus wird eine{' '}
            <strong>Silikonform</strong> abgegossen, die dann für das eigentliche{' '}
            <strong>Vergießen des Würfels</strong> mit Harz verwendet wird.
            Silikon ermöglicht das einfache Entformen auch bei komplexen Geometrien.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {steps.map((s) => (
              <div key={s.n} className={`rounded-xl border p-4 flex gap-4 ${
                isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-Template-2 shadow-sm'
              }`}>
                <div className={`text-2xl font-bold opacity-20 flex-shrink-0 ${isDarkMode ? 'text-Template-2' : 'text-Template-1'}`}>{s.n}</div>
                <div>
                  <div className={`font-bold text-sm mb-1 ${isDarkMode ? 'text-Template-2' : 'text-Template-0'}`}>{s.title}</div>
                  <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={refs[1]} className={sec(1)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Komponenten</h2>
          <hr className={hr} />
          <p className={body}>
            Alle Materialien sind auf elektronikverträgliches Vergießen ausgewählt:
            niedrig exotherm beim Aushärten, ausreichend transparent für LED-Durchlicht,
            und mechanisch stabil genug für regelmäßigen Spielbetrieb.
          </p>
          <ul className={`list-disc pl-6 space-y-2 text-sm`}>
            <li className={li}><strong>Positivform:</strong> 3D-Druck in PLA/PETG, aus CAD</li>
            <li className={li}><strong>Silikonform:</strong> Zweikomponenten-Silikon (flexibel, formstabil)</li>
            <li className={li}><strong>Gießharz:</strong> Epoxid oder Polyurethan, transparent oder getönt</li>
            <li className={li}><strong>Einlegeteile:</strong> Flex-PCB, LiPo-Akku, QI-Empfängerspule</li>
            <li className={li}><strong>Trennmittel:</strong> Silikonspray auf Positivform vor dem Silikonabguss</li>
          </ul>
        </div>
      </div>

      <div ref={refs[2]} className={sec(2)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Versuche & Erfahrungen</h2>
          <hr className={hr} />
          <p className={body}>
            Jede Iteration liefert neue Erkenntnisse – von der Oberflächenqualität der Silikonform
            bis zur optimalen Positionierung der Elektronik im Harz.
          </p>
          <ul className={`list-disc pl-6 space-y-2 text-sm`}>
            <li className={li}>Silikonformen erlauben mehrfache Nutzung – ideal für Iteration</li>
            <li className={li}>Flex-PCB muss vor dem Verguss sicher fixiert werden (Position kritisch)</li>
            <li className={li}>Luftblasen im Harz durch langsames Eingießen oder Vakuumbehandlung reduzieren</li>
            <li className={li}>Zu schnelle Aushärtung (hohe Exothermie) kann die Elektronik beschädigen</li>
            <li className={li}>Transparentes Harz ermöglicht maximale LED-Helligkeit durch das Gehäuse</li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default DiceMould;
